import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'

import {
  prependOrg,
  switchOrganization,
  useLoggedInSession,
} from '@signavio/effektif-api'
import { applicationName } from '@signavio/effektif-commons'
import { lazy as lazyMfe } from '@signavio/mfe'

import i18n from '@signavio/i18n'

import { sortBy } from 'lodash'
import invariant from 'invariant'
import { HeaderMenuItemPropsApi as GlobalHeaderMenuItemProps } from 'global-mfe/types/header'

const HeaderMenuItem = lazyMfe<GlobalHeaderMenuItemProps>(
  () => import('GlobalHeader/HeaderMenuItem')
)

const UserMenuItems = () => {
  const session = useLoggedInSession()
  const dispatch = useDispatch()
  const location = useLocation()

  const { organization: currentOrganization, organizations, user } = session

  const { licenseRequired, admin } = currentOrganization

  return (
    <>
      {organizations && organizations.length > 1 && (
        <HeaderMenuItem
          key="organizations"
          icon="organization"
          label={i18n('Organizations')}
        >
          {sortBy(organizations, 'name')
            .filter(({ disabled }) => !disabled)
            .map((organization) => {
              invariant(
                organization.name,
                'Organization name is required to render the organization switcher.'
              )

              return (
                <HeaderMenuItem
                  key={organization.id}
                  icon="organization"
                  active={organization.id === currentOrganization.id}
                  onClick={() => {
                    const organizationKey = organization.key
                    if (organizationKey === currentOrganization.key) {
                      return
                    }

                    invariant(
                      organization,
                      `Could not find organization with key "${organizationKey}" to switch to.`
                    )

                    dispatch(
                      switchOrganization({
                        oldOrganization: currentOrganization,
                        newOrganization: organization,
                        currentPath: location.pathname,
                      })
                    )
                  }}
                  label={organization.name}
                />
              )
            })}
        </HeaderMenuItem>
      )}

      {licenseRequired && (
        <HeaderMenuItem
          key="purchase-license"
          icon="currency"
          label={i18n('Purchase __applicationName__', { applicationName })}
          href={prependOrg('/buy')}
        />
      )}

      {!licenseRequired && admin && (
        <HeaderMenuItem
          key="organization-settings"
          icon="organization"
          label={i18n('Organization settings')}
          href={prependOrg('/organization')}
        />
      )}

      {!licenseRequired && admin && (
        <HeaderMenuItem
          key="services-connectors"
          icon="configure"
          label={i18n('Services & Connectors')}
          href={prependOrg('/services/connectors')}
        />
      )}

      {user.systemAdmin && (
        <HeaderMenuItem
          key="system-admin"
          icon="configure"
          label={i18n('System Administration')}
          href={prependOrg('/admin/systemAdministration/organizations')}
        />
      )}
    </>
  )
}

export { UserMenuItems }
